import Icon, {
  BoldOutlined,
  ItalicOutlined,
  PlusOutlined,
  UnderlineOutlined,
} from '@ant-design/icons';
import { useMemoizedFn } from 'ahooks';
import { Alert, Button, Dropdown, Menu, message, Tooltip } from 'antd';
import ErrorBoundary from 'components/ErrorBoundary';
import TagConcept from 'components/TemplateEditor/renders/TagConcept';
import { CustomSlateNodeType } from 'components/TemplateEditor/types';
import useModal from 'hooks/useModal';
import { ReactNode, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { createEditor, Descendant, Editor, Text, Transforms } from 'slate';
import { withHistory } from 'slate-history';
import { Editable, ReactEditor, RenderElementProps, Slate, withReact } from 'slate-react';
import ConceptForm from '../ConceptForm';
import { translateTemplate } from 'views/Aide/Scenario/PolicyForm/TranslateTool';
import StatusButton from 'components/StatusButton';
import { useTranslate } from '@tolgee/react';
import { ConceptElement, CustomText } from 'components/TemplateEditor/slateTypes';
import withConcept from 'components/TemplateEditor/lySlatePlugins/slatePlugins/withConcept';
import { withSelectionGuard } from 'components/TemplateEditor/lySlatePlugins/slatePlugins/withSelectionGuard';
import EmojiPicker from './EmojiPicker';
import LyEditor from 'components/TemplateEditor/utils/LyEditor';
import { ConceptContext } from './ConceptContext';
import { ReactComponent as ConceptSvg } from './assets/concept.svg';
import styled from '@emotion/styled';
import { langMap } from '../share';
import {
  boldFormatter,
  italicFormatter,
  strikeThroughFormatter,
} from 'components/WhatsAppTemplatePreview/whatsAppMessageParser';

export const value2editorValue = (value: Aide.WhatsAppContent[]): Descendant[] => {
  const children = value.map((item) => {
    if (item.type === 'TEXT') {
      return {
        type: CustomSlateNodeType.TEXT,
        text: item.text,
      } as CustomText;
    }
    return {
      type: CustomSlateNodeType.CONCEPT,
      label: item.concept,
      children: [{ text: '' }],
    } as ConceptElement;
  });
  return [
    {
      type: CustomSlateNodeType.PARAGRAPH,
      children:
        children.length > 0
          ? children
          : [
              {
                type: CustomSlateNodeType.TEXT,
                text: '',
              },
            ],
    },
  ];
};

export const getLength = (value: Aide.WhatsAppContent[]) => {
  return value.reduce((res, cur) => {
    if (cur.type === 'CONCEPT') {
      return res + cur.example.length;
    }
    return res + cur.text.length;
  }, 0);
};

function ContentEditor({
  lang,
  value,
  translatedValue,
  onChange,
  onTranslatedChange,
}: {
  lang: Aide.WhatsAppLanguage;
  value: Aide.WhatsAppContent[];
  translatedValue: Aide.WhatsAppContent[];
  onChange: (value: Aide.WhatsAppContent[]) => void;
  onTranslatedChange: (value: Aide.WhatsAppContent[]) => void;
}) {
  const { t } = useTranslate();

  const { editor, translatedEditor } = useMemo(() => {
    return {
      editor: withConcept(withHistory(withReact(withSelectionGuard(createEditor())))),
      translatedEditor: withConcept(withHistory(withReact(withSelectionGuard(createEditor())))),
    };
  }, []);

  const [activeEditor, setActiveEditor] = useState(editor);

  const compositionRef = useRef(false);

  useEffect(() => {
    setTimeout(() => {
      Editor.normalize(editor, { force: true });
      Editor.normalize(translatedEditor, { force: true });
    }, 50);
  }, [editor, translatedEditor]);

  const { conceptMap, onCreateConcept, getValue } = useConceptContext();

  const [editorValue, setEditorValue] = useState(() => value2editorValue(value));
  const [translatedEditorValue, setTranslatedEditorValue] = useState(() =>
    value2editorValue(translatedValue),
  );

  const valueLength = useMemo(() => getLength(value), [value]);
  const translatedValueLength = useMemo(() => getLength(translatedValue), [translatedValue]);

  const [translatedShow, setTranslatedShow] = useState(translatedValueLength > 0);

  const handleChange = useMemoizedFn((editorValue: Descendant[]) => {
    setEditorValue(editorValue);
    onChange(getValue(editorValue));
  });
  const handleTranslatedChange = useMemoizedFn((editorValue: Descendant[]) => {
    setTranslatedEditorValue(editorValue);
    onTranslatedChange(getValue(editorValue));
  });

  const wrapWith = (str: string) => {
    ReactEditor.focus(activeEditor);

    setTimeout(() => {
      if (!activeEditor.selection) {
        return;
      }

      const { anchor, focus } = activeEditor.selection;

      Transforms.insertText(activeEditor, str, { at: anchor });
      Transforms.insertText(activeEditor, str, { at: activeEditor.selection.focus });

      Transforms.setSelection(activeEditor, {
        anchor: { ...anchor, offset: anchor.offset + str.length },
        focus: { ...focus, offset: focus.offset + str.length },
      });
    });
  };

  return (
    <ErrorBoundary
      componentName="WhatsAppContentEditor"
      fallback={(_, reset) => (
        <Button type="link" onClick={reset}>
          {t('未知错误，点此恢复')}
        </Button>
      )}
    >
      <Container>
        <header>
          <Tooltip title={t('变量')}>
            <Dropdown
              overlay={
                <Menu
                  onClick={({ key }) => {
                    if (key === '@@@add') {
                      onCreateConcept();
                    } else {
                      LyEditor.insertConcept(activeEditor, key);
                    }
                  }}
                >
                  {Object.keys(conceptMap).map((concept) => {
                    return (
                      <Menu.Item key={concept}>
                        <Button size="small" type="link">
                          {concept}
                        </Button>
                      </Menu.Item>
                    );
                  })}
                  <Menu.Item key="@@@add">
                    <Button type="link" icon={<PlusOutlined />}>
                      {t('自定义变量')}
                    </Button>
                  </Menu.Item>
                </Menu>
              }
            >
              <Button type="text" icon={<Icon component={ConceptSvg} />} />
            </Dropdown>
          </Tooltip>
          <Tooltip title={t('phrase.表情')}>
            <EmojiPicker placement="bottomLeft" editor={activeEditor} />
          </Tooltip>
          <Tooltip title={t('加粗')}>
            <Button
              type="text"
              icon={<BoldOutlined />}
              onClick={() => wrapWith(boldFormatter.markupChar)}
            />
          </Tooltip>
          <Tooltip title={t('倾斜')}>
            <Button
              type="text"
              icon={<ItalicOutlined />}
              onClick={() => wrapWith(italicFormatter.markupChar)}
            />
          </Tooltip>
          <Tooltip title={t('删除线')}>
            <Button
              type="text"
              icon={<UnderlineOutlined />}
              onClick={() => wrapWith(strikeThroughFormatter.markupChar)}
            />
          </Tooltip>
        </header>
        <div>
          <section>
            {translatedShow && (
              <header>
                {!translatedValueLength && (
                  <Alert banner type="warning" message={t('phrase.最终发送内容')} />
                )}
              </header>
            )}
            <Slate editor={editor} value={editorValue} onChange={handleChange}>
              <Editable
                placeholder={t('trade.请输入')}
                renderElement={(props) => <RenderElement {...props} />}
                onFocus={() => setActiveEditor(editor)}
                onCompositionStart={() => (compositionRef.current = true)}
                onCompositionEnd={() => (compositionRef.current = false)}
                scrollSelectionIntoView={() => {}}
                onBeforeInput={() => {
                  setTimeout(() => {
                    if (editor.selection) {
                      editor.apply({
                        type: 'set_selection',
                        properties: null,
                        newProperties: {
                          anchor: editor.selection.anchor,
                          focus: editor.selection.focus,
                        },
                      });
                    }
                  });
                }}
                onKeyDown={(event) => {
                  if (compositionRef.current) {
                    event.preventDefault();
                    return;
                  }

                  if (event.key === 'Enter') {
                    event.preventDefault();
                    Transforms.insertText(editor, '\n');
                  }
                }}
              />
            </Slate>
            <footer>
              <span>
                {valueLength}
                /1024
              </span>
              <StatusButton
                type="primary"
                disabled={!valueLength}
                onClick={() =>
                  translateTemplate(editorValue, langMap[lang])
                    .then((res) => {
                      handleTranslatedChange(res);
                      setTranslatedShow(true);
                    })
                    .catch(() => {
                      message.error(t('marketing.翻译失败，请稍后再试'));
                    })
                }
              >
                {t('翻译站点语言')}
              </StatusButton>
            </footer>
          </section>
          <section style={translatedShow ? undefined : { display: 'none' }}>
            <header>
              {translatedValueLength > 0 && (
                <Alert banner type="warning" message={t('phrase.最终发送内容')} />
              )}
            </header>
            <Slate
              editor={translatedEditor}
              value={translatedEditorValue}
              onChange={handleTranslatedChange}
            >
              <Editable
                placeholder={t('trade.请输入')}
                renderElement={(props) => <RenderElement {...props} />}
                onFocus={() => setActiveEditor(translatedEditor)}
                onCompositionStart={() => (compositionRef.current = true)}
                onCompositionEnd={() => (compositionRef.current = false)}
                scrollSelectionIntoView={() => {}}
                onBeforeInput={() => {
                  setTimeout(() => {
                    if (editor.selection) {
                      editor.apply({
                        type: 'set_selection',
                        properties: null,
                        newProperties: {
                          anchor: editor.selection.anchor,
                          focus: editor.selection.focus,
                        },
                      });
                    }
                  });
                }}
                onKeyDown={(event) => {
                  if (compositionRef.current) {
                    event.preventDefault();
                    return;
                  }

                  if (event.key === 'Enter') {
                    event.preventDefault();
                    Transforms.insertText(translatedEditor, '\n');
                  }
                }}
              />
            </Slate>
            <footer>
              <span>
                {translatedValueLength}
                /1024
              </span>
            </footer>
          </section>
        </div>
      </Container>
    </ErrorBoundary>
  );
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 240px;
  border: solid 1px var(--fett-border-default);
  > header {
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    height: 40px;
    padding: 0 8px;
    background: #f9f9f9;
    border-bottom: solid 1px var(--fett-border-default);
  }
  > div {
    flex: 1 0 0;
    display: flex;
    overflow: hidden;
    > section {
      flex: 1 0 0;
      height: 100%;
      display: flex;
      flex-direction: column;
      overflow: hidden;
      > header {
        flex: 0 0 auto;
        height: 26px;
        .ant-alert {
          display: inline-flex;
          padding: 2px 14px;
        }
      }
      > footer {
        flex: 0 0 auto;
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 16px;
      }
      > div {
        flex: 1 0 0;
        padding: 12px;
        overflow-y: auto;
        overflow-x: hidden;
      }
    }
    section + section {
      border-left: solid 1px var(--fett-border-default);
    }
  }
`;

function RenderElement(props: RenderElementProps) {
  const { attributes, children, element } = props;

  switch (element.type) {
    case CustomSlateNodeType.CONCEPT: {
      return <TagConcept {...props} />;
    }
    default:
      return <div {...attributes}>{children}</div>;
  }
}

export function ConceptProvider({
  children,
  templateList,
}: {
  children: ReactNode;
  templateList: Aide.WhatsAppTemplateGroup[];
}) {
  const [conceptMap, setConceptMap] = useState(() =>
    templateList.reduce<Record<string, Aide.WhatsAppConceptContent>>((res, { template }) => {
      return template.reduce((res, { content, translateContent }) => {
        return [...content, ...translateContent].reduce((res, cur) => {
          if (cur.type === 'CONCEPT') {
            res[cur.concept] = cur;
          }
          return res;
        }, res);
      }, res);
    }, {}),
  );

  const conceptModalState = useModal();

  const conceptModal = (
    <ConceptForm
      visible={conceptModalState.visible}
      onCancel={conceptModalState.hide}
      conceptList={Object.values(conceptMap)}
      onSave={(value) => {
        setConceptMap({
          ...conceptMap,
          [value.concept]: { type: 'CONCEPT', ...value },
        });
      }}
    />
  );

  const getValue = useMemoizedFn((editorValue: Descendant[]) => {
    return editorValue.reduce<Aide.WhatsAppContent[]>((res, cur) => {
      if (cur.type === CustomSlateNodeType.CONCEPT) {
        const concept = conceptMap[cur.label];
        if (concept) {
          res.push(concept);
        }
      } else if (Text.isText(cur) && cur.text) {
        res.push({
          type: 'TEXT',
          text: cur.text,
        });
      } else if (cur.type === CustomSlateNodeType.PARAGRAPH) {
        res.push(...getValue(cur.children));
      }
      return res;
    }, []);
  });

  return (
    <ConceptContext.Provider
      value={useMemo(
        () => ({
          conceptMap,
          onCreateConcept: conceptModalState.show,
          getValue,
        }),
        [conceptMap, conceptModalState.show, getValue],
      )}
    >
      {conceptModal}
      {children}
    </ConceptContext.Provider>
  );
}

export function useConceptContext() {
  const value = useContext(ConceptContext);
  if (!value) {
    throw new Error(
      'The useConceptContext hook should be used in a context of ConceptProvider component!',
    );
  }
  return value;
}

export default ContentEditor;
